import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Alert, Button, Input, usePaymentMessages, type PaymentTitle } from '@alwindows/shared'
import { getScreenPaymentUrl } from '@alwindows/shared/api/screens'
import { useQuote } from '../quote/QuoteContext'

/**
 * Optional prefill from the URL. The legacy app never read params and the
 * current SAP approval email (as far as we know) sends a bare link — but if
 * A&L ever appends ?refNo=...&surname=... to the email template, the form
 * fills itself. Tolerates BOTH placements a hash-routed link allows:
 *   .../order-screens-online/?refNo=X#/screen/payment   (before the hash)
 *   .../order-screens-online/#/screen/payment?refNo=X   (inside the hash)
 */
function paramFromUrl(hashSearch: string, name: string): string {
  const inHash = new URLSearchParams(hashSearch).get(name)
  if (inHash) return inHash
  if (typeof window !== 'undefined') {
    return new URLSearchParams(window.location.search).get(name) ?? ''
  }
  return ''
}

/**
 * Standalone payment page. Users land here from the email link they receive
 * after Submit. They enter their reference number + surname; we resolve the
 * paymentURL via /v1/screen/generatePayment and embed it in an iframe.
 *
 * The iframe communicates via the same postMessage protocol as spare-parts —
 * see shared/src/payment/usePaymentMessages.ts.
 */
export function Payment() {
  const { quote, reset } = useQuote()
  const navigate = useNavigate()
  const location = useLocation()

  // "Start over" wipes the in-flight quote before returning to the start.
  // By the time a user is here their order is already saved in SAP (refNo
  // and all), so clearing the session can't lose anything — and NOT
  // clearing it would let a stale refNo make /screen/submit show the old
  // "Quote saved" confirmation for a brand-new quote.
  const startOver = () => {
    reset()
    navigate('/')
  }
  // Prefill order: URL params (email link) win over the in-session quote —
  // a clicked email link is more current than whatever session lingers.
  const [refNo, setRefNo] = useState(paramFromUrl(location.search, 'refNo') || quote.refNo || '')
  const [surname, setSurname] = useState(
    paramFromUrl(location.search, 'surname') || quote.customer?.lastName || '',
  )
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null)
  const [paymentTitle, setPaymentTitle] = useState<PaymentTitle>('Payment')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Move focus to the heading when the iframe posts a title-change so AT users
  // get the most important page state change announced (WCAG 4.1.3 / 2.4.3).
  const paymentTitleRef = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    if (paymentUrl && paymentTitle !== 'Payment') {
      paymentTitleRef.current?.focus()
      if (typeof document !== 'undefined') document.title = paymentTitle
    }
  }, [paymentTitle, paymentUrl])

  usePaymentMessages(paymentUrl, {
    onClearCart: () => {
      // Screens has no persistent cart — no-op. We don't reset the in-flight
      // quote here either; the user may want to see their quote on screen
      // after payment completes.
    },
    onTitleChange: (t: PaymentTitle) => setPaymentTitle(t),
  })

  const handleLookup = useCallback(async () => {
    setError(null)
    if (!refNo.trim() || !surname.trim()) {
      setError('Please enter both reference number and surname.')
      return
    }
    setLoading(true)
    try {
      const url = await getScreenPaymentUrl(refNo.trim(), surname.trim())
      setPaymentUrl(url)
      setPaymentTitle('Payment')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not find your quote.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [refNo, surname])

  if (paymentUrl) {
    return (
      <div className="al-payment">
        <h2 ref={paymentTitleRef} tabIndex={-1} aria-live="polite" aria-atomic="true">
          {paymentTitle}
        </h2>
        <iframe
          className="al-payment__iframe"
          title="Payment"
          src={paymentUrl}
          scrolling="no"
          frameBorder={0}
        />
        {paymentTitle === 'Thank you' ? (
          <Alert variant="success" title="Order paid">
            Thanks — we&apos;ve received your payment.
          </Alert>
        ) : null}
        {paymentTitle === 'Sorry' ? (
          <Alert variant="error" title="Payment unsuccessful">
            Please contact our team to retry.
          </Alert>
        ) : null}
        <div className="al-sc__actions">
          <Button variant="outline" onClick={startOver}>
            Start over
          </Button>
        </div>
      </div>
    )
  }

  // Field layout and input styling follow the same conventions as the
  // Submit form / spare-parts Checkout: theme `row`/`col-*` grid and the
  // theme's `input-text` class; neutral outline Back, red-outline CTA.
  return (
    <div className="al-payment">
      <h2>Pay for your quote</h2>
      <p>Enter the reference number from your quote email and your surname.</p>
      <div className="row">
        <div className="col-xs-12 col-md-6">
          <Input
            label="Reference number"
            required
            value={refNo}
            autoComplete="off"
            maxLength={64}
            onChange={(e) => setRefNo(e.target.value)}
            className="input-text"
          />
        </div>
        <div className="col-xs-12 col-md-6">
          <Input
            label="Surname"
            required
            value={surname}
            autoComplete="family-name"
            maxLength={35}
            onChange={(e) => setSurname(e.target.value)}
            className="input-text"
          />
        </div>
      </div>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <div className="al-sc__actions">
        <Button variant="outline" onClick={startOver}>
          Start over
        </Button>
        <Button variant="primary-outline" fill onClick={handleLookup} disabled={loading}>
          {loading ? 'Looking up…' : 'Continue to payment'}
        </Button>
      </div>
    </div>
  )
}
